import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Dialog from 'Components/UI/Dialog';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import fr from 'facet-rest';
import useCollection from 'Hooks/useCollection';
import MetaTable from 'Components/UI/Meta/MetaTable';
import PropTypes from 'prop-types';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import { removeQuery } from 'Helpers/Query';
import { pickImplementation } from 'Helpers/Affordances';
import useUserContext from 'Context/UserContext';
import moment from 'moment-timezone';
import history from 'State/history';
import { advisorHouseholdSchedulerUri } from 'Consts/advisorUris';

const styles = (theme) => ({
  root: {
    maxWidth: '50%',
  },
  text: {
    padding: theme.spacing(4),
    textAlign: 'center',
    ...theme.typography.h3,
  },
  tableContent: {
    height: '500px',
    overflowY: 'auto',
    padding: theme.spacing(0, 2),
  },
  button: {
    width: 'fit-content',
    alignSelf: 'center',
    margin: theme.spacing(4, 0),
  },
  cell: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
});

const UncategorizedMeetingModal = ({
  className,
  classes,
}) => {
  const [modalOpen, setModalOpen] = useState(true);
  const { agentResource } = useUserContext();
  const { householdResource } = useSimpleHousehold();
  const {
    collection: householdMeetingCollection,
    setCollection: setHouseholdMeetingCollection,
  } = useCollection();

  const getAllHouseholdMeetings = async () => {
    if (householdResource) {
      const householdMeetings = await householdResource
        ?.relationship(fr.relationships.previousHouseholdMeetings.name)
        .link('related')
        .get();
      setHouseholdMeetingCollection(householdMeetings);
    }
  };

  const toggleOffModal = () => setModalOpen(false);

  const handleOpeningSchedulerModal = pickImplementation(agentResource, {
    [fr.relationships.default.name]: async () => {
      toggleOffModal();
      const [nextProjectedMeeting] = await householdResource.relationship('projectedMeetings').link('related').Get({
        params: {
          tz: moment.tz.guess(),
        },
      });
      history.push(advisorHouseholdSchedulerUri(householdResource.id(), {
        link: nextProjectedMeeting.relationship(fr.relationships.projectedMeetingAvailabilityPeriod.name).link('related').url(),
        projectedMeetingLink: nextProjectedMeeting.link('self').url(),
      }));
    },
  });

  useEffect(() => {
    getAllHouseholdMeetings();
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      removeQuery('link', 'modal');
    }
  }, [modalOpen]);

  if (!householdMeetingCollection) {
    return null;
  }

  return (
    <Dialog
      open={modalOpen}
      onClose={toggleOffModal}
      className={classNames(className)}
      maxWidth={false}
      showCloseIcon={false}
      classes={{
        paper: classes.root,
      }}
    >
      <div className={classNames(classes.text)}>
        {"Meeting projections are based off of the number of held planning meetings. It looks like some of this household's meetings are not categorized, please categorize these before scheduling your next call."}
      </div>
      <div className={classes.tableContent}>
        <MetaTable
          disablePagination
          classes={{
            cell: classes.cell,
          }}
          columns={[
            { name: 'startTime', style: { width: '75px' } },
            { name: 'meetingType' },
            { name: 'title', style: { minWidth: '200px' } },
            { name: 'status', style: { width: '150px' } },
          ]}
          collection={householdMeetingCollection}
          tableMetaOverrideSetName="UncategorizedMeetingOverride"
          features={{
            edit: {
              type: 'inline',
              onSubmit: getAllHouseholdMeetings,
            },
          }}
        />
      </div>
      <Button
        disableElevation
        variant="contained"
        color="secondary"
        className={classes.button}
        onClick={handleOpeningSchedulerModal}
      >
        Schedule Call
      </Button>
    </Dialog>
  );
};

UncategorizedMeetingModal.defaultProps = {
  className: '',
};

UncategorizedMeetingModal.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(UncategorizedMeetingModal);
